/**
 * Turn the studio's live render of a project into thumbnail bytes.
 *
 * The producer that shipped before this shot `https://{slug}.cogo.xyz` through
 * Browserless. Every project starts as a draft and a draft has no live
 * subdomain — measured 2026-07-31, both sampled draft slugs answered
 * `404 App Not Found` — so that producer could never fire for the projects
 * that actually need a thumbnail. The surface used here instead is the IDE
 * build-mode canvas: the studio's own DOM render of the project's own block
 * bundle, which exists for drafts by definition.
 *
 * Honesty guard: 36 of 40 sampled projects have zero blocks, so their canvas
 * renders as a blank sheet. Rasterising that would put a white rectangle on
 * the card and call it a preview. `captureThumbnailFromElement` therefore
 * refuses to return bytes for a visually uniform capture — the card keeps its
 * placeholder, which is the truthful thing to show for an empty project.
 */

import { toCanvas } from 'html-to-image'

/** Card art is `aspect-[16/10]`; 640x400 keeps that ratio at 2x of the rendered box. */
export const THUMBNAIL_WIDTH = 640
export const THUMBNAIL_HEIGHT = 400

/** Below this the source box is chrome, not a rendered app. */
const MIN_SOURCE_EDGE_PX = 160

/**
 * Fraction of sampled pixels that must differ from the top-left reference
 * pixel for the capture to count as "has content". A blank sheet lands at 0;
 * a real render is orders of magnitude above this.
 */
const MIN_NON_UNIFORM_RATIO = 0.02

export interface ThumbnailCapture {
  blob: Blob
  width: number
  height: number
}

export type ThumbnailCaptureSkipReason =
  | 'no-element'
  | 'element-too-small'
  | 'raster-failed'
  | 'blank-render'
  | 'encode-failed'

export type ThumbnailCaptureResult =
  | { ok: true; capture: ThumbnailCapture }
  | { ok: false; reason: ThumbnailCaptureSkipReason }

export interface CaptureOptions {
  /** Test seam for the html-to-image rasteriser. */
  rasterize?: (node: HTMLElement) => Promise<HTMLCanvasElement>
  /** Test seam for canvas creation (jsdom has no real 2d context). */
  createCanvas?: (width: number, height: number) => HTMLCanvasElement
}

async function defaultRasterize(node: HTMLElement): Promise<HTMLCanvasElement> {
  return toCanvas(node, {
    // 1x keeps the capture cheap; the card art is 16:10 at a few hundred px.
    pixelRatio: 1,
    cacheBust: false,
    // Fonts are re-fetched and inlined as base64 otherwise — seconds of work
    // for a thumbnail whose text is a few pixels tall.
    skipFonts: true,
    backgroundColor: '#ffffff',
    filter: (domNode) =>
      !(domNode instanceof HTMLElement) ||
      domNode.dataset.cogoThumbnailExclude !== 'true',
  })
}

function defaultCreateCanvas(width: number, height: number): HTMLCanvasElement {
  const canvas = document.createElement('canvas')
  canvas.width = width
  canvas.height = height
  return canvas
}

/**
 * Are enough pixels different from each other for this to be a render rather
 * than a blank sheet? Returns `null` when the pixels cannot be read at all.
 */
export function measureNonUniformRatio(
  ctx: CanvasRenderingContext2D,
  width: number,
  height: number,
): number | null {
  let data: Uint8ClampedArray
  try {
    data = ctx.getImageData(0, 0, width, height).data
  } catch {
    return null
  }
  if (data.length < 4) return null

  const [r0, g0, b0] = [data[0], data[1], data[2]]
  let sampled = 0
  let different = 0
  // Sample every 7th pixel — dense enough to catch sparse content, cheap enough
  // to stay off the main thread's budget.
  for (let i = 0; i < data.length; i += 4 * 7) {
    sampled++
    if (
      Math.abs(data[i] - r0) > 8 ||
      Math.abs(data[i + 1] - g0) > 8 ||
      Math.abs(data[i + 2] - b0) > 8
    ) {
      different++
    }
  }
  return sampled === 0 ? null : different / sampled
}

/**
 * Rasterise `element` into 16:10 thumbnail bytes.
 *
 * The source box is cropped "cover, top-aligned" to match the card's
 * `object-cover object-top` so the captured image and the card agree about
 * which part of the app is visible.
 */
export async function captureThumbnailFromElement(
  element: HTMLElement | null,
  options: CaptureOptions = {},
): Promise<ThumbnailCaptureResult> {
  if (!element) return { ok: false, reason: 'no-element' }

  const rect = element.getBoundingClientRect()
  const sourceWidth = Math.round(rect.width)
  const sourceHeight = Math.round(rect.height)
  if (sourceWidth < MIN_SOURCE_EDGE_PX || sourceHeight < MIN_SOURCE_EDGE_PX) {
    return { ok: false, reason: 'element-too-small' }
  }

  let source: HTMLCanvasElement
  try {
    source = await (options.rasterize ?? defaultRasterize)(element)
  } catch {
    return { ok: false, reason: 'raster-failed' }
  }
  if (!source?.width || !source.height) return { ok: false, reason: 'raster-failed' }

  const target = (options.createCanvas ?? defaultCreateCanvas)(
    THUMBNAIL_WIDTH,
    THUMBNAIL_HEIGHT,
  )
  const ctx = target.getContext('2d')
  if (!ctx) return { ok: false, reason: 'raster-failed' }

  ctx.fillStyle = '#ffffff'
  ctx.fillRect(0, 0, THUMBNAIL_WIDTH, THUMBNAIL_HEIGHT)

  // cover + top: fill the width, take the top slice of the height.
  const scale = THUMBNAIL_WIDTH / source.width
  const drawHeight = source.height * scale
  const sliceHeight =
    drawHeight <= THUMBNAIL_HEIGHT ? source.height : THUMBNAIL_HEIGHT / scale
  ctx.drawImage(
    source,
    0,
    0,
    source.width,
    sliceHeight,
    0,
    0,
    THUMBNAIL_WIDTH,
    Math.min(THUMBNAIL_HEIGHT, drawHeight),
  )

  const nonUniform = measureNonUniformRatio(ctx, THUMBNAIL_WIDTH, THUMBNAIL_HEIGHT)
  // `null` means the canvas is tainted, which would also make toBlob throw —
  // treat it as unverifiable and refuse rather than ship an unchecked image.
  if (nonUniform === null || nonUniform < MIN_NON_UNIFORM_RATIO) {
    return { ok: false, reason: 'blank-render' }
  }

  const blob = await new Promise<Blob | null>((resolve) => {
    try {
      target.toBlob((value) => resolve(value), 'image/png')
    } catch {
      resolve(null)
    }
  })
  if (!blob) return { ok: false, reason: 'encode-failed' }

  return {
    ok: true,
    capture: { blob, width: THUMBNAIL_WIDTH, height: THUMBNAIL_HEIGHT },
  }
}
